import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, Observable, Subscription, map, take } from 'rxjs';

import { CustomPromptsManagementDialogComponent }
    from '../../../Admin/AccountManagement/components/CustomPromptsManagement/custom-prompts-management-dialog.component';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { ScrollPanelComponent } from '../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { TakeOverPanelComponent } from '../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { CustomInsights } from '../../../Monitor/Shared/models/custom-insights.model';
import { CoverageAnalysisService, InsightContext } from '../../../Monitor/Shared/services/coverage-analysis.service';
import { CurationOutcomeActions } from '../CurationAgentActions/curation-agent-actions.component';
import { CurationAgentSourceModel } from '../../models/curation-agent-source.model';
import { CurationAgentModel, curationAgentNameMaxLength } from '../../models/curation-agent.model';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { curationPromptType } from '../../enums/curation-prompt-type.const';

/** Sentinel value for the "+ New Custom Prompt" entry in the prompt dropdown */
export const newPromptOptionValue: number = -1;

@Component({
    selector: 'agpr-curation-agent-panel',
    templateUrl: './curation-agent-panel.component.html',
    styleUrls: ['./curation-agent-panel.component.scss'],
    standalone: false
})
export class CurationAgentPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly nameMaxLength = curationAgentNameMaxLength;
    readonly newPromptOption = newPromptOptionValue;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    /** Existing agent to edit. Omitted when creating */
    @Input() agent: CurationAgentModel;

    /** Whether the panel opened on an existing agent, which the heading and footer label follow */
    editing: boolean = false;

    /** Actions as of the last emission, so an addition can be told from an edit or a removal */
    private actionCount: number = 0;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;
    @ViewChild('formScroll') formScroll: ScrollPanelComponent;
    @ViewChild('actionsScroll') actionsScroll: ScrollPanelComponent;

    hideSlideOverlay: boolean = false;
    saving: boolean = false;

    promptOptions: DropdownOptionModel[] = [];
    /** Bound to the prompt dropdown - either a saved prompt id or {@link newPromptOptionValue} */
    selectedPromptOption: number = null;
    selectedPromptText: string = null;

    // Inline "+ New Custom Prompt" fields
    newPromptName: string = '';
    newPromptInstructions: string = '';
    savingPrompt: boolean = false;

    showHelp: boolean = false;

    private curationPrompts: CustomInsights[] = [];
    /** Set between saving a new prompt and its option reaching the dropdown, which selects it */
    private pendingPromptId: number = null;
    private readonly subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private coverageAnalysisService: CoverageAnalysisService,
        private curationAgentsService: CurationAgentsService
    ) {
        super(coreDialogService, 'CurationAgentPanel');
    }

    ngOnInit(): void {
        if (!this.agent) {
            this.agent = new CurationAgentModel();
        }

        // The panel always offers a row to fill in, even for an agent whose sources have all gone.
        // It lives here rather than on the loaded agent: everything else counts what it is given.
        if (!this.agent.sources.length) {
            this.agent.sources = [new CurationAgentSourceModel()];
        }

        // Fixed at open: saving assigns an id, and the title should not change under the user
        this.editing = !!this.agent.id;

        this.actionCount = this.agent.positiveActions.length + this.agent.negativeActions.length;

        this.selectedPromptOption = this.agent.customPromptId ?? null;

        this.subscriptions.push(
            this.coverageAnalysisService.customInsights$.pipe(
                map(insights => insights.filter(insight => insight.promptType === curationPromptType))
            ).subscribe(prompts => {
                this.curationPrompts = prompts;
                this.promptOptions = [
                    new DropdownOptionModel('AutomationAgents.panel.prompt.new', newPromptOptionValue),
                    ...prompts.map(prompt => new DropdownOptionModel(prompt.name, prompt.id))
                ];
                this.selectPendingPrompt();
                this.refreshSelectedPromptText();
            })
        );
    }

    get headingKey(): string {
        return this.editing ? 'AutomationAgents.panel.heading.edit' : 'AutomationAgents.panel.heading';
    }

    /** An existing agent is amended rather than saved for the first time, so the footer says Apply */
    get saveLabel(): string {
        return this.editing ? 'Core.button.apply' : 'save';
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    get creatingPrompt(): boolean {
        return this.selectedPromptOption === newPromptOptionValue;
    }

    get canSavePrompt(): boolean {
        return !!this.newPromptName?.trim() && !!this.newPromptInstructions?.trim() && !this.savingPrompt;
    }

    get canSave(): boolean {
        return this.agent.isValid() && !this.saving;
    }

    promptChanged(promptId: number): void {
        this.selectedPromptOption = promptId;

        if (this.creatingPrompt) {
            this.agent.customPromptId = null;
            this.selectedPromptText = null;
            return;
        } else {
            this.showHelp = false;
        }

        this.agent.customPromptId = promptId;
        this.newPromptName = '';
        this.newPromptInstructions = '';
        this.refreshSelectedPromptText();
    }

    savePrompt(): void {
        const prompt = new CustomInsights();
        prompt.name = this.newPromptName.trim();
        prompt.prompt = this.newPromptInstructions.trim();
        prompt.promptType = curationPromptType;

        this.savingPrompt = true;
        this.coverageAnalysisService.createCustomInsight(prompt, InsightContext.CustomPromptDialog).pipe(
            take(1)
        ).subscribe({
            next: id => {
                this.savingPrompt = false;

                if (!id) {
                    return;
                }

                // Held rather than selected outright: the dropdown resolves its label when the
                // value is written, and the option for a just-saved prompt is not in the list yet
                this.pendingPromptId = id;
                this.selectPendingPrompt();
            },
            error: () => {
                this.savingPrompt = false;
            }
        });
    }

    /** Opens the shared prompt manager, scoped to curation prompts */
    openPromptManager(): void {
        this.coreDialogService.open(CustomPromptsManagementDialogComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: this.overlayContainer,
                selectedPromptType: curationPromptType,
                // Opens on the prompt the panel has selected, so Edit lands on the right one
                selectedInsightId: this.selectedPromptOption ?? null
            }
        });
    }

    addSource(): void {
        if (!this.agent.canAddSource) {
            return;
        }

        this.agent.sources = [...this.agent.sources, new CurationAgentSourceModel()];
        // The new row is drawn below the fold once a few sources are on the agent
        this.scrollToNewest(this.formScroll);
    }

    removeSource(source: CurationAgentSourceModel): void {
        this.agent.sources = this.agent.sources.filter(item => item !== source);

        if (!this.agent.sources.length) {
            this.agent.sources = [new CurationAgentSourceModel()];
        }
    }

    actionsChanged(actions: CurationOutcomeActions): void {
        // Counted against what was last emitted, not against the agent: the actions component works
        // on the very arrays it was handed, so by now the agent already carries the new action
        const count = actions.positive.length + actions.negative.length;
        const added = count > this.actionCount;

        this.actionCount = count;
        this.agent.positiveActions = [...actions.positive];
        this.agent.negativeActions = [...actions.negative];

        // Only on an addition - removing a card should leave the list where the user was reading it
        if (added) {
            this.scrollToNewest(this.actionsScroll);
        }
    }

    /** The Add New Action card opens at the foot of the list, so it is brought into view */
    scrollActionsToEnd(): void {
        this.scrollToNewest(this.actionsScroll);
    }

    /**
     * Scrolls the column a card was just added to down to its end. Deferred twice over - the card is
     * only in the DOM after the next change detection, and the panel measures its content after that
     * - then repeated once it has settled, since a card grows as its own controls render.
     */
    private scrollToNewest(scrollPanel: ScrollPanelComponent): void {
        const toEnd = () => {
            const content = scrollPanel?.sp?.contentViewChild?.nativeElement as HTMLElement;

            scrollPanel?.scrollTo(content?.scrollHeight ?? 0, 0, false);
        };

        requestAnimationFrame(() => requestAnimationFrame(() => {
            toEnd();
            setTimeout(toEnd, 200);
        }));
    }

    save(): void {
        if (!this.canSave) {
            return;
        }

        this.saving = true;
        this.curationAgentsService.save(this.agent).pipe(take(1)).subscribe({
            next: () => {
                this.saving = false;
                this.closeDialog(this.agent);
            },
            error: () => {
                this.saving = false;
            }
        });
    }

    /**
     * Cancel runs the panel's own slide-out so it matches the X and click-outside; the dialog itself is
     * torn down by {@link onVisibleChange} once that animation finishes. close() is a no-op until the
     * panel has finished opening, so fall back to closing outright in that window.
     */
    cancel(): void {
        if (this.takeOverPanel?.isPanelFullyOpened) {
            this.takeOverPanel.close();
            return;
        }

        this.closeDialog();
    }

    /**
     * The takeover only animates itself out - the hosting dialog has to remove itself, otherwise the
     * component stays mounted on the body and the panel reappears on the next change detection pass.
     */
    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    /** Selects a prompt saved from this panel, once the refreshed list has caught up with it */
    private selectPendingPrompt(): void {
        if (!this.pendingPromptId || !this.curationPrompts.some(prompt => prompt.id === this.pendingPromptId)) {
            return;
        }

        const promptId = this.pendingPromptId;
        this.pendingPromptId = null;
        this.promptChanged(promptId);
    }

    private refreshSelectedPromptText(): void {
        const prompt = this.curationPrompts.find(item => item.id === this.agent.customPromptId);
        this.selectedPromptText = prompt?.prompt ?? null;
    }
}
